import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getFocusedWorkspaceLocation, useWorkspaceOptional } from './WorkspaceContext';
import {
  getPageHelp,
  HELP_VERSION,
  HELP_CHANGELOG,
  resolvePageHelpId,
} from '../config/portalPageHelp';

const PortalHelpContext = createContext(null);

const VERSION_STORAGE_KEY = 'portal-help-version-seen';
const pageSeenKey = (pageId) => `portal-help-seen-${HELP_VERSION}-${pageId}`;

export function PortalHelpProvider({ children }) {
  const location = useLocation();
  const workspace = useWorkspaceOptional();
  const { isAdmin, isAuditor, getUserDepartment } = useAuth();
  const helpLocation = useMemo(() => {
    const focused = getFocusedWorkspaceLocation(workspace);
    if (focused) return focused;
    return { pathname: location.pathname, search: location.search || '' };
  }, [workspace, location.pathname, location.search]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('guide');
  const [highlightSectionId, setHighlightSectionId] = useState(null);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  const pageHelp = useMemo(
    () => getPageHelp(helpLocation.pathname, helpLocation.search),
    [helpLocation.pathname, helpLocation.search],
  );

  const pageHelpId = useMemo(
    () => resolvePageHelpId(helpLocation.pathname, helpLocation.search),
    [helpLocation.pathname, helpLocation.search],
  );

  const helpAvailable = useMemo(() => {
    if (pageHelp?.adminOnly && !isAdmin() && getUserDepartment() !== 'accounts') return false;
    return true;
  }, [pageHelp, isAdmin, getUserDepartment]);

  const openHelp = useCallback((options = {}) => {
    if (options.tab) setActiveTab(options.tab);
    if (options.sectionId) {
      setActiveTab('sections');
      setHighlightSectionId(options.sectionId);
    }
    setPanelOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setPanelOpen(false);
    setHighlightSectionId(null);
  }, []);

  const toggleHelp = useCallback(() => {
    setPanelOpen((open) => !open);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    if (!sectionId) return;
    const el = document.querySelector(`[data-help-section="${sectionId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.setAttribute('data-help-highlight', 'true');
      window.setTimeout(() => el.removeAttribute('data-help-highlight'), 2400);
    }
    setHighlightSectionId(sectionId);
  }, []);

  const markPageSeen = useCallback(() => {
    if (!pageHelpId || pageHelpId === 'default') return;
    try {
      localStorage.setItem(pageSeenKey(pageHelpId), '1');
    } catch {
      // ignore
    }
  }, [pageHelpId]);

  const dismissWhatsNew = useCallback(() => {
    setShowWhatsNew(false);
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, HELP_VERSION);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    setHighlightSectionId(null);
    if (panelOpen) setActiveTab('guide');
  }, [helpLocation.pathname, helpLocation.search]);

  useEffect(() => {
    if (!panelOpen || !highlightSectionId) return undefined;
    const timer = window.setTimeout(() => scrollToSection(highlightSectionId), 300);
    return () => window.clearTimeout(timer);
  }, [panelOpen, highlightSectionId, scrollToSection, helpLocation.pathname]);

  useEffect(() => {
    try {
      const seenVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      if (seenVersion !== HELP_VERSION && HELP_CHANGELOG[HELP_VERSION]) {
        setShowWhatsNew(true);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!helpAvailable || !pageHelpId || pageHelpId === 'default') {
      setIsFirstVisit(false);
      return undefined;
    }

    let seen = false;
    try {
      seen = localStorage.getItem(pageSeenKey(pageHelpId)) === '1';
    } catch {
      seen = false;
    }

    setIsFirstVisit(!seen);

    if (seen) return undefined;

    const timer = window.setTimeout(() => {
      markPageSeen();
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [helpAvailable, pageHelpId, markPageSeen]);

  const value = useMemo(
    () => ({
      pageHelp,
      pageHelpId,
      helpAvailable,
      panelOpen,
      activeTab,
      setActiveTab,
      highlightSectionId,
      showWhatsNew,
      isFirstVisit,
      helpChangelog: HELP_CHANGELOG[HELP_VERSION],
      openHelp,
      closeHelp,
      toggleHelp,
      scrollToSection,
      markPageSeen,
      dismissWhatsNew,
      isAuditor: isAuditor(),
      isAdmin: isAdmin(),
    }),
    [
      pageHelp,
      pageHelpId,
      helpAvailable,
      panelOpen,
      activeTab,
      highlightSectionId,
      showWhatsNew,
      isFirstVisit,
      openHelp,
      closeHelp,
      toggleHelp,
      scrollToSection,
      markPageSeen,
      dismissWhatsNew,
      isAuditor,
      isAdmin,
    ],
  );

  return (
    <PortalHelpContext.Provider value={value}>
      {children}
    </PortalHelpContext.Provider>
  );
}

export function usePortalHelp() {
  const ctx = useContext(PortalHelpContext);
  if (!ctx) {
    throw new Error('usePortalHelp must be used within PortalHelpProvider');
  }
  return ctx;
}